import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import SpeedTest from '@cloudflare/speedtest';

import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { getNotification } from '@components/Notification/helpers';

import { getIsLowInternetSpeed } from '@helpers';
import { convertBitToMB } from './helpers';
import { configOptions } from './constants';
import { env } from 'env';
import { useNotificationContext } from '@providers/NotificationProvider';

/**
 * Runs the Cloudflare download-speed measurement for the connection step and
 * warns the user when the link is too slow or the measurement fails.
 *
 * The measurement is paused when the step is left — it is not resumed, stored
 * or reported anywhere else.
 *
 * @returns The measured speed in Mbps, whether the run has settled, and whether
 * a slow-connection notification was raised (`null` until it settles).
 */
export const useFastCheckInternetConnectionSpeed = () => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [isShowSpeedTestNotification, setIsShowSpeedTestNotification] =
    useState<boolean | null>(null);
  const isErrorRef = useRef<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const notifiedRef = useRef(false);

  // The measurement is rendered, so it has to be state. It is mirrored into a
  // ref because the engine callbacks and the timeout below are set up once and
  // would otherwise close over the initial 0 forever.
  const [speed, setSpeed] = useState(0);
  const speedRef = useRef<number>(0);

  const updateSpeed = useCallback((value: number) => {
    speedRef.current = value;
    setSpeed(value);
  }, []);

  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();
  const { activeNotificationId } = useNotificationContext();

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      // very slow connections, connection hanged indefinitely handling case
      // skip if there was an error querying CloudFlare servers
      // otherwise check if current speed is less than required and show notification
      if (
        !isErrorRef.current &&
        speedRef.current < +(env.REACT_APP_MIN_SPEED_THRESHOLD_MBPS || 15)
      ) {
        setIsCompleted(true);
      }
    }, +(env.REACT_APP_SPEED_CHECK_TIMEOUT_SECONDS_DEVICE_TEST || 30) * 1000);

    const speedResult = new SpeedTest(configOptions);

    const showConnectionErrorNotification = () => {
      if (isErrorRef.current) return;
      isErrorRef.current = true;
      setIsCompleted(true);
      setIsShowSpeedTestNotification(true);

      const message = `${translate(
        'notifications.unstableInternet',
      )} (Error code: CF)`;

      const notificationData = getNotification({
        message,
        type: NOTIFICATION_TYPES.WARNING,
        closeManually: true,
      });

      const notificationId = showNotification({
        ...notificationData,
        logMessage: message,
      });

      activeNotificationId.current.push(notificationId);

      sendCustomSentryBreadcrumb({ message });

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };

    speedResult.onFinish = (results) => {
      try {
        updateSpeed(convertBitToMB(results.getDownloadBandwidth() ?? 0));

        if ((speedResult.results as any)?.raw?.download?.finished) {
          setIsCompleted(true);
          isErrorRef.current = false;
        }
      } catch {
        showConnectionErrorNotification();
      }
    };

    speedResult.onError = showConnectionErrorNotification;

    speedResult.onResultsChange = () => {
      try {
        updateSpeed(
          convertBitToMB(speedResult.results.getDownloadBandwidth() ?? 0),
        );

        const rawDownload = (speedResult.results as any)?.raw?.download;

        if (rawDownload?.error && !rawDownload?.finished) {
          showConnectionErrorNotification();
        }
      } catch {
        showConnectionErrorNotification();
      }
    };

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Leaving the step has to end the measurement. The engine autostarts and
      // keeps pulling download chunks (up to 250 MB each) in the background
      // otherwise, and its callbacks stay live — showConnectionErrorNotification
      // calls showNotification directly, so a stale "unstable internet" toast
      // could surface long after the connection step is gone.
      speedResult.onFinish = () => undefined;
      speedResult.onError = () => undefined;
      speedResult.onResultsChange = () => undefined;
      speedResult.pause();
    };
  }, []);

  useEffect(() => {
    if (!isCompleted || isErrorRef.current || notifiedRef.current) {
      return;
    }

    // Raised once per run: `speed` now changes on every sample, so without this
    // a measurement that keeps reporting after it settled would stack toasts.
    notifiedRef.current = true;

    const isLowInternetSpeed = getIsLowInternetSpeed(speedRef.current);
    if (!isLowInternetSpeed) {
      setIsShowSpeedTestNotification(false);
      return;
    }

    const message = `Internet connection speed is ${speedRef.current} Mbps`;

    const notificationData = getNotification({
      message: translate('notifications.unstableInternet'),
      type: NOTIFICATION_TYPES.WARNING,
      closeManually: true,
    });

    const notificationId = showNotification({
      ...notificationData,
      logMessage: message,
    });
    activeNotificationId.current.push(notificationId);

    sendCustomSentryBreadcrumb({ message });
    setIsShowSpeedTestNotification(true);
  }, [isCompleted, translate, showNotification]);

  return {
    speed,
    isCompleted,
    isShowSpeedTestNotification,
  };
};
